import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mail, Phone, MapPin, ChevronDown, Menu, X, Globe } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

// Build a usable URL for an uploaded media path (filename, '/uploads/...' or full URL)
const mediaUrl = (v) => !v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/')) ? v : '/uploads/' + v;

// ==================== BLOCK RENDERERS ====================

function RenderBlock({ block }) {
  const c = block.content || {};

  switch (block.block_type) {
    case 'hero':
      return (
        <section className="py-20 px-6" style={{ backgroundColor: c.background_color || '#1e40af' }}>
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{c.title}</h1>
            {c.subtitle && <p className="text-xl md:text-2xl opacity-90 mb-6">{c.subtitle}</p>}
            {c.text && <p className="text-lg opacity-80 mb-8 max-w-2xl mx-auto">{c.text}</p>}
            {c.cta_text && (
              <a href={c.cta_url || '#'} className="inline-block px-8 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-gray-100 transition text-lg">
                {c.cta_text}
              </a>
            )}
          </div>
        </section>
      );

    case 'heading':
      const Tag = `h${c.level || 2}`;
      const sizes = { 1: 'text-3xl md:text-4xl', 2: 'text-2xl md:text-3xl', 3: 'text-xl md:text-2xl', 4: 'text-lg', 5: 'text-base', 6: 'text-sm' };
      return (
        <section className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <Tag className={`font-bold text-gray-800 ${sizes[c.level || 2]}`} style={{ textAlign: c.align || 'left' }}>{c.text}</Tag>
          </div>
        </section>
      );

    case 'text':
      return (
        <section className="py-6 px-6">
          <div className="max-w-4xl mx-auto prose prose-gray" dangerouslySetInnerHTML={{ __html: c.html || '' }} />
        </section>
      );

    case 'features':
      const cols = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-2 lg:grid-cols-4' };
      return (
        <section className="py-12 px-6">
          <div className={`max-w-6xl mx-auto grid grid-cols-1 ${cols[c.columns] || 'md:grid-cols-3'} gap-8`}>
            {(c.items || []).map((item, i) => {
              const Icon = LucideIcons[item.icon] || LucideIcons.Star;
              return (
                <div key={i} className="text-center p-6">
                  <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon size={28} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </section>
      );

    case 'cta':
      return (
        <section className="py-12 px-6 bg-gray-50">
          <div className="max-w-3xl mx-auto text-center">
            {c.title && <h2 className="text-2xl font-bold text-gray-800 mb-3">{c.title}</h2>}
            {c.text && <p className="text-gray-600 mb-6">{c.text}</p>}
            {c.button_text && (
              <a href={c.button_url || '#'} className="inline-block px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition">
                {c.button_text}
              </a>
            )}
          </div>
        </section>
      );

    case 'testimonials':
      return (
        <section className="py-12 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {(c.items || []).map((t, i) => (
                <div key={i} className="bg-white p-6 rounded-xl shadow-sm border">
                  <p className="text-gray-600 italic mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-sm font-bold">
                      {(t.name || '?')[0]}
                    </div>
                    <div>
                      <div className="font-medium text-gray-800 text-sm">{t.name}</div>
                      {t.company && <div className="text-xs text-gray-500">{t.company}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'faq':
      return (
        <section className="py-12 px-6">
          <div className="max-w-3xl mx-auto space-y-3">
            {(c.items || []).map((item, i) => (
              <details key={i} className="bg-white border rounded-lg group">
                <summary className="px-5 py-4 cursor-pointer font-medium text-gray-800 flex items-center justify-between">
                  {item.question}
                  <ChevronDown size={18} className="text-gray-400 group-open:rotate-180 transition" />
                </summary>
                <div className="px-5 pb-4 text-gray-600 text-sm">{item.answer}</div>
              </details>
            ))}
          </div>
        </section>
      );

    case 'html':
      return <section className="py-6 px-6"><div className="max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: c.code || '' }} /></section>;

    default:
      return null;
  }
}

// ==================== SEO HELPER ====================

function updateSEO(site, page) {
  const siteName = site?.site_name || 'SWIBI';
  const pageTitle = page?.title || 'Strona glowna';
  const description = page?.meta_description || site?.site_description || 'System Wymiany Informacji Biznesowej';

  // Title
  document.title = page?.is_homepage ? siteName : `${pageTitle} | ${siteName}`;

  // Meta description
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
  metaDesc.content = description;

  // Meta keywords
  if (page?.meta_keywords) {
    let metaKw = document.querySelector('meta[name="keywords"]');
    if (!metaKw) { metaKw = document.createElement('meta'); metaKw.name = 'keywords'; document.head.appendChild(metaKw); }
    metaKw.content = page.meta_keywords;
  }

  // Open Graph
  const ogTags = {
    'og:title': document.title,
    'og:description': description,
    'og:type': 'website',
    'og:site_name': siteName,
    'og:url': window.location.href,
  };
  Object.entries(ogTags).forEach(([prop, content]) => {
    let tag = document.querySelector(`meta[property="${prop}"]`);
    if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', prop); document.head.appendChild(tag); }
    tag.content = content;
  });

  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
  canonical.href = window.location.href;

  // Schema.org — Organization
  let schema = document.querySelector('script[data-schema="organization"]');
  if (!schema) { schema = document.createElement('script'); schema.type = 'application/ld+json'; schema.setAttribute('data-schema', 'organization'); document.head.appendChild(schema); }
  schema.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    description: site?.site_description || '',
    url: window.location.origin,
  });

  // Schema.org — WebPage
  let wpSchema = document.querySelector('script[data-schema="webpage"]');
  if (!wpSchema) { wpSchema = document.createElement('script'); wpSchema.type = 'application/ld+json'; wpSchema.setAttribute('data-schema', 'webpage'); document.head.appendChild(wpSchema); }
  wpSchema.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: description,
    url: window.location.href,
    isPartOf: { '@type': 'WebSite', name: siteName, url: window.location.origin },
  });
}

// ==================== PUBLIC SITE PAGE ====================

export default function PublicSitePage() {
  const navigate = useNavigate();
  const { slug: urlSlug, pageSlug: urlPageSlug } = useParams();
  const siteSlug = urlSlug || 'default';
  const { t, i18n } = useTranslation();
  const toggleLang = () => { const next = i18n.language === 'pl' ? 'en' : 'pl'; i18n.changeLanguage(next); localStorage.setItem('swibi_language', next); };
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [menus, setMenus] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // index of open desktop dropdown
  const navRef = useRef(null);
  const [consent, setConsent] = useState(() => { try { return localStorage.getItem('cms_consent'); } catch { return 'all'; } });
  const chooseConsent = (v) => { try { localStorage.setItem('cms_consent', v); } catch {} setConsent(v); };

  // Close desktop dropdown on click outside
  useEffect(() => {
    if (openMenu === null) return;
    const handler = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  // Load site data
  useEffect(() => {
    api.get(`/cms/public/site/${siteSlug}`)
      .then(r => {
        setSite(r.data.site);
        setPages(r.data.pages);
        setMenus(r.data.menus);
        // Load requested page or homepage
        if (urlPageSlug) {
          loadPage(urlPageSlug);
        } else {
          const home = r.data.pages.find(p => p.is_homepage);
          if (home) loadPage(home.slug);
          else setLoading(false);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  const loadPage = async (slug) => {
    // Empty/"/" → resolve to the actual homepage (its slug may not be "home")
    const home = pages.find(p => p.is_homepage);
    const s = slug || home?.slug;
    if (!s) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/cms/public/site/${siteSlug}/page/${s}`);
      setCurrentPage(r.data.page);
      setBlocks(r.data.blocks);
      updateSEO(site, r.data.page);
    } catch {}
    finally { setLoading(false); }
  };

  const mainMenu = menus.find(m => m.location === 'main');
  const menuItems = mainMenu?.items || [];
  const footerMenu = menus.find(m => m.location === 'footer');
  const footerItems = footerMenu?.items || [];
  const homeSlug = pages.find(p => p.is_homepage)?.slug;

  const childrenOf = (item) => (Array.isArray(item.children) ? item.children : []).filter(c => c && (c.label || c.url));

  const isExternal = (url) => /^https?:\/\//i.test(url || '');
  const handleMenuClick = (item) => {
    if (isExternal(item.url)) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    } else {
      loadPage((item.url || '').replace(/^\/+/, '')); // "/" → "" → homepage
    }
  };
  const isActive = (item) => !isExternal(item.url) && currentPage?.slug === (((item.url || '').replace(/^\/+/, '')) || homeSlug);

  if (!site && !loading) {
    // No site — show basic landing with login redirect
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <button onClick={toggleLang} className="flex items-center gap-1 px-3 py-1.5 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition">
            <Globe size={14} /> {i18n.language === 'pl' ? 'English' : 'Polski'}
          </button>
        </div>
        <div className="text-center text-white">
          <img src="/logo.png" alt="SWIBI" className="h-16 mx-auto mb-6 brightness-0 invert" />
          <p className="text-xl opacity-80 mb-8">System Wymiany Informacji Biznesowej</p>
          <button onClick={() => navigate('/login')} className="px-8 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-gray-100 transition">
            {t('auth.loginButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: site?.font_family || 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => loadPage('')}>
            {site?.logo_filename
              ? <img src={mediaUrl(site.logo_filename)} alt={site?.site_name || ''} className="h-9 max-w-[180px] object-contain" />
              : <span className="text-lg font-bold text-gray-800">{site?.site_name || 'Strona'}</span>}
          </div>

          {/* Desktop menu */}
          <nav ref={navRef} className="hidden md:flex items-center gap-6">
            {menuItems.map((item, i) => {
              const kids = childrenOf(item);
              if (kids.length === 0) {
                return (
                  <button key={i} onClick={() => handleMenuClick(item)}
                    className={`text-sm font-medium transition ${isActive(item) ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}>
                    {item.label}
                  </button>
                );
              }
              const open = openMenu === i;
              return (
                <div key={i} className="relative">
                  <button onClick={() => setOpenMenu(open ? null : i)}
                    className={`flex items-center gap-1 text-sm font-medium transition ${open || isActive(item) ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}>
                    {item.label}
                    <ChevronDown size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="absolute left-0 top-full pt-2 z-50">
                      <div className="bg-white border border-gray-100 rounded-lg shadow-lg py-1 min-w-[200px]">
                        {item.url && (
                          <button onClick={() => { handleMenuClick(item); setOpenMenu(null); }}
                            className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition border-b border-gray-50">
                            {item.label}
                          </button>
                        )}
                        {kids.map((c, ci) => (
                          <button key={ci} onClick={() => { handleMenuClick(c); setOpenMenu(null); }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition">
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button className="md:hidden p-2" onClick={() => setMobileMenu(!mobileMenu)} aria-label="Menu">
              {mobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenu && (
          <div className="md:hidden border-t bg-white px-6 py-4 space-y-2">
            {menuItems.map((item, i) => {
              const kids = childrenOf(item);
              return (
                <div key={i}>
                  <button onClick={() => { handleMenuClick(item); setMobileMenu(false); }}
                    className="block w-full text-left py-2 text-sm font-medium text-gray-700 hover:text-blue-600">{item.label}</button>
                  {kids.length > 0 && (
                    <div className="pl-4 border-l border-gray-100 space-y-1">
                      {kids.map((c, ci) => (
                        <button key={ci} onClick={() => { handleMenuClick(c); setMobileMenu(false); }}
                          className="block w-full text-left py-1.5 text-sm text-gray-500 hover:text-blue-600">{c.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </header>

      {/* Content */}
      <main>
        {loading ? (
          <div className="py-20 text-center text-gray-400">Ladowanie...</div>
        ) : (
          blocks.map(block => <RenderBlock key={block.id} block={block} />)
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {site?.logo_filename
                  ? <img src={mediaUrl(site.logo_filename)} alt={site?.site_name || ''} className="h-8 max-w-[160px] object-contain brightness-0 invert" />
                  : <span className="text-lg font-bold text-white">{site?.site_name || ''}</span>}
              </div>
              <p className="text-sm">{site?.site_description}</p>
            </div>
            {footerItems.length > 0 ? (
              <div className="md:col-span-2 grid grid-cols-2 gap-8">
                {footerItems.map((col, i) => {
                  const kids = childrenOf(col);
                  return (
                    <div key={i}>
                      <h3 className="font-semibold text-white mb-3 text-sm">
                        {col.url
                          ? <button onClick={() => handleMenuClick(col)} className="hover:text-white transition">{col.label}</button>
                          : col.label}
                      </h3>
                      {kids.map((c, ci) => (
                        <button key={ci} onClick={() => handleMenuClick(c)}
                          className="block text-sm hover:text-white transition mb-1">{c.label}</button>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                <div>
                  <h3 className="font-semibold text-white mb-3 text-sm">Produkt</h3>
                  {menuItems.slice(0, 4).map((item, i) => (
                    <button key={i} onClick={() => handleMenuClick(item)}
                      className="block text-sm hover:text-white transition mb-1">{item.label}</button>
                  ))}
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-3 text-sm">Firma</h3>
                  <button onClick={() => loadPage('kontakt')} className="block text-sm hover:text-white transition mb-1">Kontakt</button>
                  <span className="block text-sm mb-1">Regulamin</span>
                  <span className="block text-sm mb-1">Polityka prywatnosci</span>
                </div>
              </>
            )}
            <div>
              <h3 className="font-semibold text-white mb-3 text-sm">Kontakt</h3>
              <div className="space-y-2 text-sm">
                {site?.business_email && <div className="flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${site.business_email}`} className="hover:text-white">{site.business_email}</a></div>}
                {site?.business_phone && <div className="flex items-center gap-2"><Phone size={14} /> <a href={`tel:${site.business_phone.replace(/\s/g, '')}`} className="hover:text-white">{site.business_phone}</a></div>}
                {(site?.business_street || site?.business_city) && (() => {
                  const addr = [site.business_street, [site.business_postal_code, site.business_city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                  const dest = (site.geo_lat != null && site.geo_lng != null) ? `${site.geo_lat},${site.geo_lng}` : addr;
                  return (
                    <div className="flex items-center gap-2"><MapPin size={14} className="flex-shrink-0" />
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`}
                        target="_blank" rel="noopener noreferrer" className="hover:text-white">{addr}</a>
                    </div>
                  );
                })()}
                {!site?.business_email && !site?.business_phone && !site?.business_city && (
                  <div className="text-gray-500">Uzupełnij dane firmy w ustawieniach CMS.</div>
                )}
              </div>
              {Array.isArray(site?.social_links) && site.social_links.filter(Boolean).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {site.social_links.filter(Boolean).map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white">
                      {u.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            &copy; {new Date().getFullYear()} {site?.site_name || 'SWIBI'}. Wszystkie prawa zastrzezone.
          </div>
        </div>
      </footer>

      {/* Cookie consent banner */}
      {!consent && (
        <div className="fixed inset-x-0 bottom-0 z-[300] bg-gray-900 text-gray-200 px-5 py-4">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
            <p className="text-sm max-w-2xl text-center sm:text-left">
              Używamy plików cookie do celów statystycznych. Możesz zaakceptować wszystkie lub tylko niezbędne.
              {(() => { const pp = pages.find(p => /polityk|prywat|cookie/i.test(p.slug) || /polityk|prywat|cookie/i.test(p.title)); return pp ? <> <button onClick={() => loadPage(pp.slug)} className="underline hover:text-white">Dowiedz się więcej</button>.</> : null; })()}
            </p>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => chooseConsent('all')} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: site?.primary_color || '#2563EB' }}>Akceptuję wszystkie</button>
              <button onClick={() => chooseConsent('essential')} className="px-4 py-2 rounded-lg text-sm border border-gray-600 text-gray-200 hover:bg-gray-800">Tylko niezbędne</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
